import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useContext, useEffect, useState } from "react";
import { setFileversionIdAndNodeOffsetIdMap } from "../reducers/common";
import { HoopsViewerContext, CubeGlobal } from "@Features/viewer-core";
import _ from "lodash";
import { getFileversionIdFromAwsfilename } from "./cubeViewHelper";
import { RootState } from "@Reducers/index";
import { selectFileVersionData } from "@Reducers/fileVersion";

// `fileVersionId` is the viewer's currently-open file version, which is absent when no file is
// mounted; the `filesData` lookups below then miss and the effect skips the mapping fetch.
// RETURN LEFT AS `any`, and it is load-bearing: the map is `Record<string, number>` (as
// MetaDataModal already annotates), but under `noUncheckedIndexedAccess` that makes every
// `mapper[id]` read `number | undefined`, and useViewerSelection subtracts the result without a
// guard after an `Object.hasOwn` check TS cannot use to narrow. That consumer is outside this
// module, so the map is handed out untyped and the state below carries the real shape.
//
// RE-ATTEMPTED. Return position, so method shorthand does not apply, and no call site launders the
// result through an `as`. The blocker is `noUncheckedIndexedAccess` at useViewerSelection, which
// subtracts `mapper[id]` after an `Object.hasOwn` guard tsc cannot narrow through — closing it means
// an erased `!` at that read, in a file this module does not own.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- return position: consumer subtracts mapper[id] unguarded
const useFileVersionIdAndNodeOffsetIdMap = (fileVersionId: string | undefined): any => {
  const dispatch = useDispatch();
  const filesData = useSelector((state: RootState) => state.filesData);
  const { isModelStructureReady } = useContext(HoopsViewerContext);

  // fileVersionId → HOOPS node-id offset, the same shape the fileversionIdAndNodeOffsetIdMap
  // slice stores and every consumer of this hook already annotates.
  const [mapper, setMapper] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchFileVersionMapping = () => {
      const newMapper: Record<string, number> = {};
      const absoluteNode = CubeGlobal.hwv.model.getAbsoluteRootNode();
      const modelFileNodes = CubeGlobal.hwv.model.getNodeChildren(absoluteNode);

      // `CubeGlobal.hwv` is untyped, so `getNodeChildren` returns no element type. HOOPS' `NodeId`
      // is a numeric alias; spelled out rather than imported to keep this helper hoops-free.
      modelFileNodes.forEach((node: number) => {
        const awsFilename = CubeGlobal.hwv.model.getModelFileNameFromNode(node);
        const fileVersionIdFromAws = getFileversionIdFromAwsfilename(awsFilename);
        if (fileVersionIdFromAws) {
          const nodeOffset = CubeGlobal.hwv.model.getNodeIdOffset(node);
          newMapper[fileVersionIdFromAws] = nodeOffset;
        }
      });

      dispatch(setFileversionIdAndNodeOffsetIdMap(newMapper));
      setMapper(newMapper);
    };

    const fileData = selectFileVersionData(filesData, fileVersionId);
    // Holds `undefined` in the single-file branch when no file is mounted — see the note on the
    // hook's parameter; both `Object.hasOwn` probes below then miss and the effect skips.
    const fileVersions: (string | undefined)[] =
      fileData && fileData.resourceType == "RESOURCE_TYPE_MERGED_FILE"
        ? (fileData.sourceFileVersionId ?? []).filter((file) => !file.isDeleted).map((file) => file.id)
        : [fileVersionId];
    if (
      isModelStructureReady &&
      // `val!` is erased: a possibly-undefined id is passed through verbatim, and `Object.hasOwn`
      // simply reports false for it — guarding here would change the behaviour.
      !fileVersions.every((val) => Object.hasOwn(mapper, val!)) &&
      fileVersions.every((val) => Object.hasOwn(filesData, val!))
    ) {
      fetchFileVersionMapping();
    }
  }, [fileVersionId, filesData, mapper, isModelStructureReady]);

  return mapper;
};

export default useFileVersionIdAndNodeOffsetIdMap;
